import { ErrorOAuth2, OAuth2AuthCodePKCE } from '@bity/oauth2-auth-code-pkce'
import { CancelFunc, wrapDispatch } from 'lib/cancel'
import { AccessContext, getOauthConfig, handleLogin, nextPathLocalStorageKey } from 'lib/oauth'
import { codeToString, convertError, GetReturn, Status } from 'lib/rpc'
import { NextRouter } from 'next/router'
import { Code } from 'pbts/google/rpc/code'

export enum SessionAction {
    load,
}

export type SessionActions = LoadAction

export type LoadAction = {
    type: SessionAction.load
    payload: GetReturn<AccessContext>
    client: OAuth2AuthCodePKCE | null
}

export const expiredSession = (
    dispatcher: React.Dispatch<SessionActions>,
    router: NextRouter,
    client: OAuth2AuthCodePKCE,
): CancelFunc => {
    const [dispatch, cancel] = wrapDispatch(dispatcher)
    dispatch({ type: SessionAction.load, payload: { status: Status.LOADING }, client: null })

    sessionStorage.setItem(nextPathLocalStorageKey, router.asPath)

    handleLogin(client)
    return cancel
}

export const loadSession = (dispatcher: React.Dispatch<SessionActions>, router: NextRouter): CancelFunc => {
    const [dispatch, cancel] = wrapDispatch(dispatcher)
    const doLoadSession = async (): Promise<void> => {
        const onAccessTokenExpiry = (refreshAccessToken: () => Promise<AccessContext>): Promise<AccessContext> => {
            return refreshAccessToken()
        }
        const onInvalidGrant = (refreshAuthCodeOrRefreshToken: () => Promise<void>): void => {
            // NORETURN: calls `location.replace`
            refreshAuthCodeOrRefreshToken()
        }

        const oauthClient = getOauthConfig(onAccessTokenExpiry, onInvalidGrant)

        let isReturning = false
        try {
            isReturning = await oauthClient.isReturningFromAuthServer()
        } catch (err) {
            dispatch({
                type: SessionAction.load,
                payload: {
                    status: Status.ERROR,
                    error: convertError(err),
                },
                client: oauthClient,
            })
            return
        }

        let accessContext: AccessContext
        try {
            accessContext = await oauthClient.getAccessToken()
        } catch (err) {
            if (err instanceof ErrorOAuth2) {
                dispatch({
                    type: SessionAction.load,
                    payload: {
                        status: Status.ERROR,
                        error: convertError({
                            message: err.toString(),
                            code: codeToString(Code.INTERNAL),
                        }),
                    },
                    client: oauthClient,
                })
            } else {
                dispatch({
                    type: SessionAction.load,
                    payload: { status: Status.ERROR, error: convertError(err) },
                    client: oauthClient,
                })
            }
            return
        }
        if (isReturning) {
            const nextPage = sessionStorage.getItem(nextPathLocalStorageKey)
            if (nextPage) {
                sessionStorage.removeItem(nextPathLocalStorageKey)
                router.push(nextPage)
            } else {
                router.push('/')
            }
        }
        dispatch({
            type: SessionAction.load,
            payload: { status: Status.OK, item: accessContext },
            client: oauthClient,
        })
    }

    doLoadSession()

    return (): void => {
        cancel()
    }
}
